/**
 * Category-related utilities for MetricsSearch component
 * Handles category definitions, query parameters, and category behavior
 */

import { msg } from '@gonfalon/messages/msg';
import type { GetMetricGroupsQueryParams } from '@gonfalon/openapi/operations/getMetricGroups';
import type { GetMetricsQueryParams } from '@gonfalon/openapi/operations/getMetrics';

// Category key constants
export const METRICS_CATEGORIES = {
  CURRENTLY_SELECTED: 'currently-selected',
  MOST_USED: 'most-used',
  RECENTLY_ADDED: 'recently-added',
  RELEASE_GUARDRAILS: 'release-guardrails',
  AUTOGENERATED: 'autogenerated',
  AGENT_CONTROL: 'agent-control',
  USER_DEFINED: 'user-defined',
  WAREHOUSE_NATIVE: 'warehouse-native',
  ALL: 'all',
  METRIC_GROUPS: 'metric-groups',
  FUNNEL_GROUPS: 'funnel-groups',
} as const;

// Sort field constants
export const SORT_FIELDS = {
  CONNECTION_COUNT_DESC: '-connectionCount' as const,
  CREATED_AT_DESC: '-createdAt' as const,
  NAME_ASC: 'name' as const,
} as const;

export interface Category {
  key: string;
  name: string;
}

export type CategoryGroup = Category[];

/**
 * Get available categories based on what types of items are allowed
 */
export const getCategories = (
  allowMetrics: boolean = true,
  allowMetricGroups: boolean = true,
  allowWarehouseNativeMetrics: boolean = false,
  releaseGuardrailsEnabled: boolean = false,
): CategoryGroup[] => {
  const filterReleaseGuardrails = (group: Array<{ key: string; name: string }>) =>
    releaseGuardrailsEnabled ? group : group.filter((c) => c.key !== METRICS_CATEGORIES.RELEASE_GUARDRAILS);

  const secondAllCategoriesGroup: Array<{ key: string; name: string }> = [
    { key: METRICS_CATEGORIES.AUTOGENERATED, name: msg('metrics.metricsSearch.autogeneratedCategory') },
    { key: METRICS_CATEGORIES.AGENT_CONTROL, name: msg('metrics.metricsSearch.agentControlCategory') },
    { key: METRICS_CATEGORIES.USER_DEFINED, name: msg('metrics.metricsSearch.userDefinedCategory') },
    { key: METRICS_CATEGORIES.WAREHOUSE_NATIVE, name: msg('metrics.metricsSearch.warehouseNativeCategory') },
  ];
  secondAllCategoriesGroup.push({
    key: METRICS_CATEGORIES.METRIC_GROUPS,
    name: msg('metrics.metricsSearch.standardGroupsCategory'),
  });
  secondAllCategoriesGroup.push({
    key: METRICS_CATEGORIES.FUNNEL_GROUPS,
    name: msg('metrics.metricsSearch.funnelGroupsCategory'),
  });

  const firstCategoryGroup = filterReleaseGuardrails([
    { key: METRICS_CATEGORIES.CURRENTLY_SELECTED, name: msg('metrics.metricsSearch.currentlySelectedCategory') },
    { key: METRICS_CATEGORIES.MOST_USED, name: msg('metrics.metricsSearch.mostUsedCategory') },
    { key: METRICS_CATEGORIES.RECENTLY_ADDED, name: msg('metrics.metricsSearch.recentlyAddedCategory') },
    { key: METRICS_CATEGORIES.RELEASE_GUARDRAILS, name: msg('metrics.metricsSearch.releaseGuardrailsCategory') },
  ]);

  const allCategories: CategoryGroup[] = [
    firstCategoryGroup,
    secondAllCategoriesGroup,
    [{ key: METRICS_CATEGORIES.ALL, name: msg('metrics.metricsSearch.allMetricsCategory') }],
  ];

  if (!allowMetrics && allowMetricGroups) {
    // Only metric groups allowed - show only metric groups categories
    const groupsOnlyFirstGroup = filterReleaseGuardrails([
      { key: METRICS_CATEGORIES.CURRENTLY_SELECTED, name: msg('metrics.metricsSearch.currentlySelectedCategory') },
      { key: METRICS_CATEGORIES.RELEASE_GUARDRAILS, name: msg('metrics.metricsSearch.releaseGuardrailsCategory') },
    ]);

    return [
      groupsOnlyFirstGroup,
      [{ key: METRICS_CATEGORIES.METRIC_GROUPS, name: msg('metrics.metricsSearch.standardGroupsCategory') }],
      [{ key: METRICS_CATEGORIES.FUNNEL_GROUPS, name: msg('metrics.metricsSearch.funnelGroupsCategory') }],
    ];
  }

  // Start with all categories or filter based on allowMetricGroups
  let categories = allCategories;

  if (allowMetrics && !allowMetricGroups) {
    // Only metrics allowed - remove metric groups from the categories
    categories = categories
      .map((group) =>
        group.filter(
          (category) =>
            category.key !== METRICS_CATEGORIES.METRIC_GROUPS && category.key !== METRICS_CATEGORIES.FUNNEL_GROUPS,
        ),
      )
      .filter((group) => group.length > 0);
  }

  // Filter out warehouse-native if not allowed
  if (!allowWarehouseNativeMetrics) {
    categories = categories
      .map((group) => group.filter((category) => category.key !== METRICS_CATEGORIES.WAREHOUSE_NATIVE))
      .filter((group) => group.length > 0);
  }

  return categories;
};

type MetricsFilter = NonNullable<GetMetricsQueryParams['filter']>;

/**
 * Build search filter object if search value is provided
 */
const buildSearchFilter = (searchValue?: string): MetricsFilter | undefined =>
  searchValue ? { query: searchValue } : undefined;

/**
 * Build complete filter object by merging base filter with optional search
 */
const buildFilter = (baseFilter: Partial<MetricsFilter>, searchValue?: string): MetricsFilter => ({
  ...baseFilter,
  ...(searchValue && { query: searchValue }),
});

type MetricGroupFilter = NonNullable<GetMetricGroupsQueryParams['filter']>;

/**
 * Build metric group filter (for getMetricGroupsQueryParams)
 */
const buildMetricGroupFilter = (baseFilter: Partial<MetricGroupFilter>, searchValue?: string): MetricGroupFilter => ({
  ...baseFilter,
  ...(searchValue && { query: searchValue }),
});

type MetricsSort = GetMetricsQueryParams['sort'];

/**
 * Build query parameters with common structure
 */
const buildQueryParams = (params: {
  limit: number;
  offset?: number;
  sort?: MetricsSort;
  filter?: MetricsFilter;
  includeReleasePolicyCount?: boolean;
}): GetMetricsQueryParams => {
  const baseExpand: GetMetricsQueryParams['expand'] = [
    'experimentCount',
    'metricGroupCount',
    'guardedRolloutCount',
    ...(params.includeReleasePolicyCount ? (['releasePolicyCount'] as const) : []),
  ];

  const { includeReleasePolicyCount: _, ...restParams } = params;
  return {
    expand: baseExpand,
    ...restParams,
  };
};

/**
 * Get query parameters for a specific category
 */
export const getQueryParamsForCategory = (
  category: string,
  searchValue?: string,
  offset = 0,
  includeReleasePolicyCount = false,
): GetMetricsQueryParams => {
  const limit = 15;

  switch (category) {
    case METRICS_CATEGORIES.MOST_USED: {
      return buildQueryParams({
        limit,
        sort: SORT_FIELDS.CONNECTION_COUNT_DESC,
        includeReleasePolicyCount,
      });
    }
    case METRICS_CATEGORIES.RECENTLY_ADDED: {
      return buildQueryParams({
        limit,
        sort: SORT_FIELDS.CREATED_AT_DESC,
        includeReleasePolicyCount,
      });
    }
    case METRICS_CATEGORIES.RELEASE_GUARDRAILS: {
      return buildQueryParams({
        limit: 50,
        offset: 0,
        sort: SORT_FIELDS.NAME_ASC,
        filter: { usedIn: 'release_policies' },
        includeReleasePolicyCount,
      });
    }
    case METRICS_CATEGORIES.AUTOGENERATED: {
      return buildQueryParams({
        limit,
        offset,
        filter: buildFilter({ tags: ['autogenerated'] }, searchValue),
        includeReleasePolicyCount,
      });
    }
    case METRICS_CATEGORIES.AGENT_CONTROL: {
      return buildQueryParams({
        limit,
        offset,
        sort: SORT_FIELDS.NAME_ASC,
        // "AI" is set by aiCategory in autogen/definitions/autogenerated_metrics_ai.go
        filter: buildFilter({ tags: ['AI', 'autogenerated'] }, searchValue),
        includeReleasePolicyCount,
      });
    }
    case METRICS_CATEGORIES.USER_DEFINED: {
      return buildQueryParams({
        limit,
        offset,
        sort: SORT_FIELDS.NAME_ASC,
        filter: buildFilter({ tagsNotContains: ['autogenerated'] }, searchValue),
        includeReleasePolicyCount,
      });
    }
    case METRICS_CATEGORIES.WAREHOUSE_NATIVE: {
      return buildQueryParams({
        limit,
        offset,
        sort: SORT_FIELDS.NAME_ASC,
        filter: buildFilter({ dataSourceKeysNotEquals: ['launchdarkly-hosted'] }, searchValue),
        includeReleasePolicyCount,
      });
    }
    case METRICS_CATEGORIES.ALL: {
      return buildQueryParams({
        limit,
        offset,
        sort: SORT_FIELDS.NAME_ASC,
        filter: buildSearchFilter(searchValue),
        includeReleasePolicyCount,
      });
    }
    default: {
      return buildQueryParams({
        limit,
        sort: SORT_FIELDS.NAME_ASC,
        filter: buildSearchFilter(searchValue),
        includeReleasePolicyCount,
      });
    }
  }
};

/**
 * Get query parameters for metric groups
 */
export const getMetricGroupsQueryParams = (
  category?: string,
  searchValue?: string,
  offset = 0,
  includeReleasePolicyCount = false,
) => {
  const limit = 15;

  const baseExpand: GetMetricGroupsQueryParams['expand'] = [
    'experimentCount',
    'guardedRolloutCount',
    ...(includeReleasePolicyCount ? (['releasePolicyCount'] as const) : []),
  ];

  // Release guardrails: metrics/groups used in guarded rollouts, paginate until all loaded, search on frontend
  if (category === METRICS_CATEGORIES.RELEASE_GUARDRAILS) {
    return {
      limit: 50,
      offset: 0,
      sort: 'name' as const,
      filter: { usedIn: 'release_policies' as const },
      expand: baseExpand,
    };
  }

  if (!category) {
    return {
      limit,
      offset,
      sort: 'name' as const, // Sort by name A-Z
      filter: searchValue ? { query: searchValue } : undefined,
      expand: baseExpand,
    };
  }

  switch (category) {
    case METRICS_CATEGORIES.METRIC_GROUPS: {
      return {
        limit,
        offset,
        sort: 'name' as const, // Sort by name A-Z
        filter: buildMetricGroupFilter({ kind: 'standard' }, searchValue),
        expand: baseExpand,
      };
    }
    case METRICS_CATEGORIES.FUNNEL_GROUPS: {
      return {
        limit,
        offset,
        sort: 'name' as const, // Sort by name A-Z
        filter: buildMetricGroupFilter({ kind: 'funnel' }, searchValue),
        expand: baseExpand,
      };
    }
    default: {
      return {
        limit,
        offset,
        sort: 'name' as const, // Sort by name A-Z
        filter: searchValue ? { query: searchValue } : undefined,
        expand: baseExpand,
      };
    }
  }
};

/**
 * Check if a category shows a filtered subset of metrics; searching within one of these
 * also searches across all metrics so matches outside the category can be shown separately
 */
export const categorySupportsAllMetricsSearch = (category: string): boolean =>
  category === METRICS_CATEGORIES.MOST_USED ||
  category === METRICS_CATEGORIES.RECENTLY_ADDED ||
  category === METRICS_CATEGORIES.AUTOGENERATED ||
  category === METRICS_CATEGORIES.AGENT_CONTROL ||
  category === METRICS_CATEGORIES.USER_DEFINED ||
  category === METRICS_CATEGORIES.WAREHOUSE_NATIVE;

/**
 * Check if a category supports infinite scroll/pagination
 */
export const categorySupportsInfiniteScroll = (category: string): boolean =>
  category === METRICS_CATEGORIES.AUTOGENERATED ||
  category === METRICS_CATEGORIES.AGENT_CONTROL ||
  category === METRICS_CATEGORIES.USER_DEFINED ||
  category === METRICS_CATEGORIES.WAREHOUSE_NATIVE ||
  category === METRICS_CATEGORIES.ALL ||
  category === METRICS_CATEGORIES.METRIC_GROUPS ||
  category === METRICS_CATEGORIES.FUNNEL_GROUPS;

/**
 * Determine the default category based on props and selections
 * Ignores defaultCategory if it's release-guardrails but that category is hidden
 */
export const getDefaultCategory = (
  defaultCategory: string | undefined,
  hasDefaultSelections: boolean,
  allowMetrics: boolean,
  allowMetricGroups: boolean,
  releaseGuardrailsEnabled: boolean = false,
): string => {
  // If defaultCategory prop is provided and valid (not release-guardrails when hidden), use it
  if (defaultCategory) {
    if (defaultCategory === METRICS_CATEGORIES.RELEASE_GUARDRAILS && !releaseGuardrailsEnabled) {
      // Fall through to normal logic
    } else {
      return defaultCategory;
    }
  }

  if (hasDefaultSelections) {
    return METRICS_CATEGORIES.CURRENTLY_SELECTED;
  }

  if (!allowMetrics && allowMetricGroups) {
    return METRICS_CATEGORIES.METRIC_GROUPS;
  }

  return METRICS_CATEGORIES.MOST_USED;
};
